import { ClassificationResult } from '@jump-tech-frontend/domain';
import { AnalysisStatus, adviceTexts, statusOf } from '@jump-tech-frontend/image-analysis';
import { AiValidationI18n, ImageVerdictVm, VerdictTone } from './image-capture.model';

interface VerdictStyle {
  tone: VerdictTone;
  icon: string;
  title: (i18n: AiValidationI18n) => string;
}

const VERDICT_STYLES: Record<AnalysisStatus, VerdictStyle> = {
  success: {
    tone: 'success',
    icon: 'check',
    title: i18n => i18n?.imageValidationSuccessful || 'Image Validation Successful'
  },
  quality_issues: {
    tone: 'warning',
    icon: 'error',
    title: i18n => i18n?.imageQualityIssues || 'Image Quality Issues'
  },
  low_confidence: {
    tone: 'warning',
    icon: 'warning',
    title: i18n => i18n?.lowConfidenceDetection || 'Low Confidence Detection'
  },
  wrong_equipment: {
    tone: 'warning',
    icon: 'swap_horiz',
    title: i18n => i18n?.wrongEquipmentDetected || 'Wrong Equipment Detected'
  },
  no_detection: {
    tone: 'info',
    icon: 'info',
    title: i18n => i18n?.noEquipmentDetected || 'No Equipment Detected'
  },
  in_progress: {
    tone: 'low',
    icon: 'psychology',
    title: i18n => i18n?.imageValidationInProgress || 'Image Validation In Progress'
  },
  error: {
    tone: 'danger',
    icon: 'error',
    title: i18n => i18n?.imageValidationFailed || 'Image Validation Failed'
  }
};

const EQUIPMENT_LABELS: Record<string, (i18n: AiValidationI18n) => string> = {
  'fuse-cutout': i18n => i18n?.fuseCutout || 'Fuse Cutout',
  'consumer-unit': i18n => i18n?.consumerUnit || 'Consumer Unit',
  'isolator-switch': i18n => i18n?.isolatorSwitch || 'Isolator Switch'
};

export function equipmentToggleLabel(classification: string | undefined, i18n: AiValidationI18n): string {
  const label = classification ? EQUIPMENT_LABELS[classification] : undefined;
  return label ? label(i18n) : i18n?.detection || 'Detection';
}

export function analysingVerdict(i18n: AiValidationI18n): ImageVerdictVm {
  const style = VERDICT_STYLES.in_progress;
  return {
    tone: style.tone,
    icon: style.icon,
    title: style.title(i18n),
    message: i18n?.analyzingImage || 'Analysing image...',
    analysing: true
  };
}

export function unavailableVerdict(i18n: AiValidationI18n): ImageVerdictVm {
  const style = VERDICT_STYLES.error;
  return {
    tone: style.tone,
    icon: style.icon,
    title: style.title(i18n),
    message: i18n?.failedToValidateImage || 'Failed to validate image. You can still use it.',
    analysing: false
  };
}

export function buildVerdictVm(result: ClassificationResult, i18n: AiValidationI18n): ImageVerdictVm {
  const style = VERDICT_STYLES[statusOf(result)];
  return {
    tone: style.tone,
    icon: style.icon,
    title: style.title(i18n),
    message: adviceTexts(result, 'message', i18n).join(' '),
    analysing: false
  };
}
